// Rug Room Shots: pick an ACTIVE base room for one shape (build spec 9.4). Same-style rooms come first,
// then the rooms of the right type for the rug (allowedLetters, spec 3.1), then the least-used room
// (generations + planned targets, then room code), which matches the backend's "least-used" rule.
// Every room shows its letter (L · Living room (accent chair), ...); a room that is not the usual type for the
// rug or whose base photo shows an oval rug is marked, but may still be chosen (the server flags it).
// scene 'sun' (the sunroom shot of an outdoor + sunroom rug) lists Sunroom (SU) rooms only (spec 3.10).
// multiple: tick several rooms (values / onChangeMany), used by bulk regenerate "Choose the room for each group".
// allowedLetters may hold several rugs' specs joined by '|' (a bulk group, roomRules.groupLettersSpec): a room is
// the usual type when any of them allows its letter.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Chip, Tooltip, Paper } from '@mui/material';
import { MdCheckCircle } from 'react-icons/md';
import rrsApi from './api';
import { Tile, StyleChip, errText, AI_COLOR } from './common';
import {
  letterName, letterAcceptableAny, describeLettersAny, baseShapeLabel, baseShapeTip, roomLetterOfRoom, roomBaseShapeBad,
} from './roomRules';

export const roomUsage = (r) => ((r.usage && r.usage.generations) || 0) + ((r.usage && r.usage.plannedTargets) || 0);
export const roomLetter = roomLetterOfRoom;
export const baseShapeBad = roomBaseShapeBad;

export const sortRoomsFor = (rooms, styleCode, lettersSpec) => [...(rooms || [])].sort((a, b) => {
  const as = a.styleCode === styleCode ? 0 : 1;
  const bs = b.styleCode === styleCode ? 0 : 1;
  if (as !== bs) return as - bs;
  const aa = letterAcceptableAny(lettersSpec, roomLetter(a)) && !baseShapeBad(a) ? 0 : 1;
  const ba = letterAcceptableAny(lettersSpec, roomLetter(b)) && !baseShapeBad(b) ? 0 : 1;
  if (aa !== ba) return aa - ba;
  const au = roomUsage(a);
  const bu = roomUsage(b);
  if (au !== bu) return au - bu;
  return String(a.roomCode).localeCompare(String(b.roomCode));
});

const smallChipSx = { height: 20, fontSize: '0.65rem' };

const RoomPicker = ({
  shapeCode, styleCode, value, onChange, styles, currentRoomCode, onLoaded, onPreview,
  allowedLetters: lettersSpec, scene, multiple, values, onChangeMany, letterNames, maxHeight = 420,
}) => {
  const [rooms, setRooms] = useState(null);
  const [error, setError] = useState('');
  const twin = scene === 'sun';
  // Held in a ref so an inline onLoaded callback cannot re-trigger the fetch.
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => { onLoadedRef.current = onLoaded; }, [onLoaded]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await rrsApi.listRooms({ type: shapeCode, status: 'active' });
      const pool = (res.items || []).filter((r) => !twin || r.styleCode === 'SU');
      const sorted = sortRoomsFor(pool, styleCode, lettersSpec);
      setRooms(sorted);
      if (onLoadedRef.current) onLoadedRef.current(sorted);
    } catch (e) {
      setError(errText(e));
      setRooms([]);
    }
  }, [shapeCode, styleCode, lettersSpec, twin]);

  useEffect(() => { load(); }, [load]);

  if (rooms === null) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rooms.length) {
    return (
      <Alert severity="warning">
        {twin
          ? 'There is no active Sunroom room for this shape yet. Sunroom shots use Sunroom rooms; activate one in the Rooms tab.'
          : 'There is no active room for this shape yet. Activate one in the Rooms tab.'}
      </Alert>
    );
  }

  const picked = multiple ? (values || []) : [];
  const toggle = (r) => {
    if (!multiple) { onChange(r.roomCode, r); return; }
    const next = picked.includes(r.roomCode) ? picked.filter((c) => c !== r.roomCode) : [...picked, r.roomCode];
    if (onChangeMany) onChangeMany(next, rooms.filter((x) => next.includes(x.roomCode)));
  };

  return (
    <Box>
      {twin && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
          Sunroom shots use Sunroom rooms.
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.25, maxHeight, overflowY: 'auto', p: 0.5 }}>
        {rooms.map((r) => {
          const selected = multiple ? picked.includes(r.roomCode) : value === r.roomCode;
          const otherStyle = r.styleCode !== styleCode;
          const letter = roomLetter(r);
          const notUsual = !!lettersSpec && !letterAcceptableAny(lettersSpec, letter);
          const ovalBase = baseShapeBad(r);
          return (
            <Paper
              key={r.roomCode}
              variant="outlined"
              onClick={() => toggle(r)}
              sx={{
                p: 1, cursor: 'pointer', borderWidth: 2, position: 'relative',
                borderColor: selected ? AI_COLOR : 'divider',
                bgcolor: selected ? `${AI_COLOR}14` : 'transparent',
                '&:hover': { borderColor: selected ? AI_COLOR : 'text.disabled' },
              }}
            >
              <Tile
                src={r.base && r.base.smallUrl} alt={r.roomCode} size={130}
                sx={{ width: '100%', height: 130 }}
                onClick={onPreview ? (e) => { e.stopPropagation(); onPreview(r); } : undefined}
              >
                {multiple && selected && (
                  <Box sx={{ position: 'absolute', top: 4, right: 4, color: AI_COLOR, bgcolor: '#fff', borderRadius: '50%', display: 'flex' }}>
                    <MdCheckCircle size={22} />
                  </Box>
                )}
              </Tile>
              <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.roomCode}</Typography>
                {currentRoomCode === r.roomCode && <Chip size="small" label="current" sx={{ height: 18, fontSize: '0.65rem' }} />}
              </Box>
              {letter && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap title={r.letterName || letterName(letter, letterNames)}>
                  {r.letterName || letterName(letter, letterNames)}
                </Typography>
              )}
              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <StyleChip code={r.styleCode} styles={styles} />
                {otherStyle && (
                  <Tooltip title="This room belongs to a different style; the result will be flagged room_style_mismatch.">
                    <Chip size="small" label="other style" color="warning" variant="outlined" sx={smallChipSx} />
                  </Tooltip>
                )}
                {notUsual && (
                  <Tooltip title={`Not the usual room type for ${multiple ? 'these rugs: they usually use' : 'this rug: it usually uses'} ${describeLettersAny(lettersSpec, letterNames)} rooms. You can still choose it.`}>
                    <Chip size="small" label="not the usual room type" color="warning" variant="outlined" sx={smallChipSx} />
                  </Tooltip>
                )}
                {ovalBase && (
                  <Tooltip title={baseShapeTip(r.base)}>
                    <Chip size="small" label={baseShapeLabel(r.base)} color="warning" sx={smallChipSx} />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                used {roomUsage(r)} ({(r.usage && r.usage.generations) || 0} gen, {(r.usage && r.usage.plannedTargets) || 0} planned)
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default RoomPicker;
